/**
 * Every TanStack Query key, in one place.
 *
 * Centralised so realtime invalidation can be surgical: a message arriving
 * patches the open thread and invalidates only the counters, rather than
 * refetching the world.
 *
 * EVERY key begins with the workspace id. Two workspaces can never collide in
 * the cache, and `queryClient.clear()` on switch is belt-and-braces on top.
 */

export const keys = {
  auth: {
    session: ['auth', 'session'] as const,
  },

  workspaces: {
    all: ['workspaces'] as const,
    mine: () => [...keys.workspaces.all, 'mine'] as const,
    detail: (ws: string) => [...keys.workspaces.all, ws] as const,
    access: (ws: string) => [...keys.workspaces.all, ws, 'access'] as const,
  },

  chatbots: {
    all: (ws: string) => ['chatbots', ws] as const,
    list: (ws: string) => [...keys.chatbots.all(ws), 'list'] as const,
    detail: (ws: string, id: string) => [...keys.chatbots.all(ws), id] as const,
    settings: (ws: string, id: string) =>
      [...keys.chatbots.detail(ws, id), 'settings'] as const,
    readiness: (ws: string, id: string, target: string) =>
      [...keys.chatbots.detail(ws, id), 'readiness', target] as const,
    prompt: (ws: string, id: string) =>
      [...keys.chatbots.detail(ws, id), 'prompt'] as const,
    domains: (ws: string, id: string) =>
      [...keys.chatbots.detail(ws, id), 'domains'] as const,
  },

  knowledge: {
    all: (ws: string) => ['knowledge', ws] as const,
    sources: (ws: string) => [...keys.knowledge.all(ws), 'sources'] as const,
    documents: (ws: string, sourceId: string) =>
      [...keys.knowledge.all(ws), 'sources', sourceId, 'documents'] as const,
    gaps: (ws: string) => [...keys.knowledge.all(ws), 'gaps'] as const,
  },

  inbox: {
    all: (ws: string) => ['inbox', ws] as const,
    list: (ws: string, query: Record<string, unknown>) =>
      [...keys.inbox.all(ws), 'list', query] as const,
    counts: (ws: string, query: Record<string, unknown>) =>
      [...keys.inbox.all(ws), 'counts', query] as const,
    thread: (ws: string, id: string) => [...keys.inbox.all(ws), 'thread', id] as const,
  },

  team: {
    all: (ws: string) => ['team', ws] as const,
    members: (ws: string) => [...keys.team.all(ws), 'members'] as const,
    invites: (ws: string) => [...keys.team.all(ws), 'invites'] as const,
    roles: (ws: string) => [...keys.team.all(ws), 'roles'] as const,
    permissions: (ws: string) => [...keys.team.all(ws), 'permissions'] as const,
    departments: (ws: string) => [...keys.team.all(ws), 'departments'] as const,
    departmentDetail: (ws: string, id: string) =>
      [...keys.team.departments(ws), id] as const,
  },

  automations: {
    all: (ws: string) => ['automations', ws] as const,
    list: (ws: string) => [...keys.automations.all(ws), 'list'] as const,
    detail: (ws: string, id: string) => [...keys.automations.all(ws), id] as const,
    runs: (ws: string, id: string) => [...keys.automations.all(ws), id, 'runs'] as const,
  },

  tickets: {
    all: (ws: string) => ['tickets', ws] as const,
    list: (ws: string, query?: Record<string, unknown>) =>
      [...keys.tickets.all(ws), 'list', query ?? {}] as const,
    detail: (ws: string, id: string) => [...keys.tickets.all(ws), id] as const,
    similar: (ws: string, id: string) => [...keys.tickets.all(ws), id, 'similar'] as const,
  },

  integrations: {
    all: (ws: string) => ['integrations', ws] as const,
    list: (ws: string) => [...keys.integrations.all(ws), 'list'] as const,
    detail: (ws: string, id: string) => [...keys.integrations.all(ws), id] as const,
    logs: (ws: string, id: string) => [...keys.integrations.all(ws), id, 'logs'] as const,
  },

  developers: {
    all: (ws: string) => ['developers', ws] as const,
    apiKeys: (ws: string) => [...keys.developers.all(ws), 'api-keys'] as const,
    webhooks: (ws: string) => [...keys.developers.all(ws), 'webhooks'] as const,
    webhookDeliveries: (ws: string, id: string) =>
      [...keys.developers.all(ws), 'webhooks', id, 'deliveries'] as const,
    apiLogs: (ws: string) => [...keys.developers.all(ws), 'logs'] as const,
  },

  products: {
    all: (ws: string) => ['products', ws] as const,
    list: (ws: string, filters?: Record<string, unknown>) =>
      [...keys.products.all(ws), 'list', filters ?? {}] as const,
    detail: (ws: string, id: string) => [...keys.products.all(ws), id] as const,
  },

  commerce: {
    all: (ws: string) => ['commerce', ws] as const,
    orders: (ws: string, query?: Record<string, unknown>) =>
      [...keys.commerce.all(ws), 'orders', query ?? {}] as const,
    orderDetail: (ws: string, id: string) => [...keys.commerce.all(ws), 'orders', id] as const,
    attribution: (ws: string, orderId: string) =>
      [...keys.commerce.all(ws), 'attribution', orderId] as const,
  },

  bookings: {
    all: (ws: string) => ['bookings', ws] as const,
    list: (ws: string, query?: Record<string, unknown>) =>
      [...keys.bookings.all(ws), 'list', query ?? {}] as const,
    slots: (ws: string) => [...keys.bookings.all(ws), 'slots'] as const,
    detail: (ws: string, id: string) => [...keys.bookings.all(ws), id] as const,
  },

  billing: {
    all: (ws: string) => ['billing', ws] as const,
    plans: (ws: string) => [...keys.billing.all(ws), 'plans'] as const,
    subscription: (ws: string) => [...keys.billing.all(ws), 'subscription'] as const,
    usage: (ws: string) => [...keys.billing.all(ws), 'usage'] as const,
    invoices: (ws: string) => [...keys.billing.all(ws), 'invoices'] as const,
    invoiceDetail: (ws: string, id: string) =>
      [...keys.billing.all(ws), 'invoices', id] as const,
    managed: (ws: string) => [...keys.billing.all(ws), 'managed'] as const,
  },

  invites: {
    preview: (token: string) => ['invites', 'preview', token] as const,
  },

  /**
   * Platform administration is the one namespace NOT keyed by workspace — it is
   * about the platform, not about any customer's data.
   */
  admin: {
    all: ['admin'] as const,
    dashboard: () => [...keys.admin.all, 'dashboard'] as const,
    workspaces: (filters?: Record<string, unknown>) =>
      [...keys.admin.all, 'workspaces', filters] as const,
    workspaceDetail: (id: string) => [...keys.admin.all, 'workspaces', id] as const,
    users: (filters?: Record<string, unknown>) => [...keys.admin.all, 'users', filters] as const,
    userDetail: (id: string) => [...keys.admin.all, 'users', id] as const,
    supportAll: () => [...keys.admin.all, 'support'] as const,
    supportList: (filters?: Record<string, unknown>) =>
      [...keys.admin.all, 'support', 'list', filters ?? {}] as const,
    supportCounts: () => [...keys.admin.all, 'support', 'counts'] as const,
    supportTicket: (id: string) => [...keys.admin.all, 'support', 'ticket', id] as const,
    plans: () => [...keys.admin.all, 'plans'] as const,
    subscriptions: (filters?: Record<string, unknown>) =>
      [...keys.admin.all, 'subscriptions', filters] as const,
    payments: (filters?: Record<string, unknown>) =>
      [...keys.admin.all, 'payments', filters] as const,
    costs: (filters?: Record<string, unknown>) => [...keys.admin.all, 'costs', filters] as const,
    margins: (filters?: Record<string, unknown>) =>
      [...keys.admin.all, 'margins', filters] as const,
    topSpenders: (filters?: Record<string, unknown>) =>
      [...keys.admin.all, 'top-spenders', filters] as const,
    budgets: () => [...keys.admin.all, 'budgets'] as const,
    flags: () => [...keys.admin.all, 'flags'] as const,
    abuse: () => [...keys.admin.all, 'abuse'] as const,
    settings: () => [...keys.admin.all, 'settings'] as const,
    killSwitch: () => [...keys.admin.all, 'kill-switch'] as const,
    jobsQueues: () => [...keys.admin.all, 'jobs', 'queues'] as const,
    jobsDeadLetter: () => [...keys.admin.all, 'jobs', 'dead-letter'] as const,
    audit: (filters?: Record<string, unknown>) => [...keys.admin.all, 'audit', filters] as const,
    providers: () => [...keys.admin.all, 'ai', 'providers'] as const,
    models: () => [...keys.admin.all, 'ai', 'models'] as const,
    routing: () => [...keys.admin.all, 'ai', 'routing'] as const,
    resellers: (search?: string) => [...keys.admin.all, 'resellers', search ?? ''] as const,
    resellerDetail: (id: string) => [...keys.admin.all, 'resellers', 'detail', id] as const,
    resellerLedger: (id: string) => [...keys.admin.all, 'resellers', 'detail', id, 'ledger'] as const,
    resellerTransactions: (filters: Record<string, string>) =>
      [...keys.admin.all, 'resellers', 'transactions', filters] as const,
    resellerPayments: (status: string) =>
      [...keys.admin.all, 'resellers', 'payments', status] as const,
    resellerClients: (id: string) =>
      [...keys.admin.all, 'resellers', 'detail', id, 'clients'] as const,
  },

  /**
   * The reseller panel. Like admin, not keyed by workspace: a reseller is not a
   * member of its clients' workspaces, and every query is scoped server-side to
   * the reseller the signed-in user operates.
   */
  reseller: {
    all: ['reseller'] as const,
    me: () => [...keys.reseller.all, 'me'] as const,
    plans: () => [...keys.reseller.all, 'plans'] as const,
    packs: () => [...keys.reseller.all, 'packs'] as const,
    clients: (search?: string) => [...keys.reseller.all, 'clients', search ?? ''] as const,
    client: (id: string) => [...keys.reseller.all, 'client', id] as const,
    quote: (clientId: string, planKey: string) =>
      [...keys.reseller.all, 'quote', clientId, planKey] as const,
    ledger: (filters?: Record<string, unknown>) =>
      [...keys.reseller.all, 'ledger', filters ?? {}] as const,
    topups: () => [...keys.reseller.all, 'topups'] as const,
    requests: (status: string) => [...keys.reseller.all, 'requests', status] as const,
    alerts: () => [...keys.reseller.all, 'alerts'] as const,
  },

  /** Support tickets the signed-in person raised with Convo. Not workspace-scoped. */
  support: {
    all: ['support'] as const,
    mine: () => [...keys.support.all, 'mine'] as const,
    ticket: (id: string) => [...keys.support.all, 'ticket', id] as const,
    unread: () => [...keys.support.all, 'unread'] as const,
  },
} as const
